import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';

interface CodeExecutionStepData {
    step_type: string;
    tool: string;
    code: string;
    observations?: string;
}

export const AnalyzingIndicator = ({
    isAnalyzingStep,
    isAnalyzing,
    codeExecutionSteps
}: {
    isAnalyzingStep: boolean,
    isAnalyzing: boolean,
    codeExecutionSteps?: CodeExecutionStepData[]
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    // console.log('dropdownOpen', dropdownOpen, 'codeExecutionSteps', codeExecutionSteps);
    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };
    const getLanguageFromTool = (tool: string): string => {
        switch (tool.toLowerCase()) {
            case 'python_interpreter':
                return 'python';
            case 'javascript':
                return 'javascript';
            case 'r_interpreter':
                return 'r';
            case 'sql':
                return 'sql';
            default:
                return 'text';
        }
    };

    return (
        <div className="pb-3">
            <div
                className={`flex items-center gap-2  ${isAnalyzingStep && codeExecutionSteps && codeExecutionSteps.length > 0 ? 'cursor-pointer rounded px-2 py-1 -mx-2 -my-1' : ''}`}
                onClick={isAnalyzingStep && codeExecutionSteps && codeExecutionSteps.length > 0 ? toggleDropdown : undefined}
            >
                <span className={`text-sm font-medium text-primary dark:text-true-white ${isAnalyzing ? 'analyzed-flicker' : ''}`}>
                    {isAnalyzing ? 'Analyzing' : isAnalyzingStep ? 'Analyzed' : ""}
                </span>
                {isAnalyzing ? (
                    <span className="text-sm font-medium text-primary dark:text-true-white flex items-center gap-0.5">
                        <span className="loading-dot">.</span>
                        <span className="loading-dot">.</span>
                        <span className="loading-dot">.</span>
                    </span>
                ) : isAnalyzingStep ? (
                    dropdownOpen ? (
                        <ChevronUp className="h-4 w-4 text-primary dark:text-true-white cursor-pointer hover:text-primary/80" onClick={toggleDropdown} />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-primary dark:text-true-white cursor-pointer hover:text-primary/80" onClick={toggleDropdown} />
                    )
                ) : null}
            </div>

            {/* Show code execution steps when analyzed and dropdown is open */}
            {isAnalyzingStep && codeExecutionSteps && codeExecutionSteps.length > 0 && (
                <div className={`mt-3 transition-all duration-500 ease-in-out overflow-hidden ${dropdownOpen ? 'max-h-[600px] overflow-y-auto opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                    <div className="rounded-lg p-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-card shadow-sm">
                        {(() => {
                            // Show last 2 if 3+, show all if 2, show 1 if 1
                            let stepsToShow;
                            if (codeExecutionSteps.length >= 3) {
                                stepsToShow = codeExecutionSteps.slice(-2); // Last 2 steps
                            } else {
                                stepsToShow = codeExecutionSteps; // Show all (1 or 2 steps)
                            }

                            let markdownContent = '';

                            stepsToShow.forEach((step, index) => {
                                markdownContent += `#### Step ${index + 1}\n\n`;
                                markdownContent += `\`\`\`${getLanguageFromTool(step.tool)}\n`;
                                markdownContent += step.code;
                                markdownContent += '\n```\n\n';

                                // Skip output/observations as requested
                            });

                            return (
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                    components={{
                                        h2: ({ children }) => (
                                            <h2 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">{children}</h2>
                                        ),
                                        h3: ({ children }) => (
                                            <h3 className="text-md font-medium mb-2 text-gray-600 dark:text-gray-400">{children}</h3>
                                        ),
                                        code: ({ inline, className, children, ...props }: any) => {
                                            const match = /language-(\w+)/.exec(className || '');
                                            if (!inline && match) {
                                                return (
                                                    <SyntaxHighlighter
                                                        style={dracula}
                                                        language={match[1]}
                                                        PreTag="pre"
                                                        customStyle={{
                                                            backgroundColor: '#282C34',
                                                            padding: '1rem',
                                                            borderRadius: '0.375rem',
                                                            overflowX: 'auto',
                                                            fontSize: '0.875rem',
                                                        }}
                                                        {...props}
                                                    >
                                                        {String(children).replace(/\n$/, '')}
                                                    </SyntaxHighlighter>
                                                );
                                            }
                                            return (
                                                <code
                                                    className="rounded-[7px] border border-gray-300/60 bg-[#f0f1f3] px-1.5 py-0.5 font-mono text-sm text-[#ab5152] dark:border-gray-600 dark:bg-muted dark:text-[#fca5a5]"
                                                    {...props}
                                                >
                                                    {children}
                                                </code>
                                            );
                                        },
                                        pre: ({ children }) => (
                                            <pre className="w-full bg-gray-800 text-gray-100 rounded p-2 overflow-x-auto mb-2">
                                                {children}
                                            </pre>
                                        ),
                                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                    }}
                                >
                                    {markdownContent}
                                </ReactMarkdown>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};
